import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddressCheckDto,
  ApiLogDto,
  EnrollmentCosts,
  EnrollmentDto,
  EnrollmentEventDto,
  EnrollmentListItemDto,
  EnrollmentPaymentInput,
  EnrollmentPlanOption,
  EnrollmentStartInput,
  EnrollmentStep2Input,
  EnrollmentStep3Input,
  Paged,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/** Prepaid/postpaid enrollment (PC1142, PC1170 to PC1172) server calls. */

const oneKey = (id: string | undefined) => ['enrollments', 'one', id] as const;

/* ---------------------------------- Lists --------------------------------- */

export const useEnrollments = (params: Query) =>
  useQuery({
    queryKey: ['enrollments', 'list', params],
    queryFn: () => api.get<Paged<EnrollmentListItemDto>>('/enrollments', params),
    placeholderData: (prev) => prev,
  });

/** "Enrollment lifecycle" dialog. Read fresh every time it opens. */
export const useEnrollmentLifecycle = (id: string | undefined) =>
  useQuery({
    queryKey: ['enrollments', 'lifecycle', id],
    queryFn: () => api.get<{ data: EnrollmentEventDto[] }>(`/enrollments/${id}/lifecycle`),
    select: (r) => r.data,
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 0,
  });

/** Per-enrollment API log dialog. Read fresh every time it opens. */
/** "USPS address request response": the address checks of one order. */
export const useEnrollmentAddressChecks = (id: string | undefined) =>
  useQuery({
    queryKey: ['enrollments', 'address-checks', id],
    queryFn: () =>
      api.get<{ data: AddressCheckDto[] }>(`/enrollments/${id}/address-checks`).then((r) => r.data),
    enabled: Boolean(id),
  });

export const useEnrollmentApiLog = (id: string | undefined) =>
  useQuery({
    queryKey: ['enrollments', 'api-log', id],
    queryFn: () => api.get<{ data: ApiLogDto[] }>(`/enrollments/${id}/api-log`),
    select: (r) => r.data,
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 0,
  });

/* --------------------------------- Wizard --------------------------------- */

export const useEnrollment = (id: string | undefined) =>
  useQuery({
    queryKey: oneKey(id),
    queryFn: () => api.get<EnrollmentDto>(`/enrollments/${id}`),
    enabled: Boolean(id),
  });

export interface EnrollmentPlans {
  data: EnrollmentPlanOption[];
  /** System setting "Email Mandatory": whether step 2 needs an email address. */
  emailMandatory: boolean;
}

/** Plans offered at step 2 for the enrollment's state and the user's sales channel. */
export const useEnrollmentPlans = (id: string) =>
  useQuery({
    queryKey: ['enrollments', 'plans', id],
    queryFn: () => api.get<EnrollmentPlans>(`/enrollments/${id}/plans`),
  });

/** "Overall Cost Details" for a number of advance months; the last answer stays while the next loads. */
/** `modelId` is the model of a shipment order; its price is the handset cost. */
export const useEnrollmentCosts = (id: string, advanceMonths: number, modelId?: string) =>
  useQuery({
    queryKey: ['enrollments', 'costs', id, advanceMonths, modelId ?? ''],
    queryFn: () => api.get<EnrollmentCosts>(`/enrollments/${id}/costs`, { advanceMonths, modelId }),
    placeholderData: (prev) => prev,
  });

/**
 * A wizard step answers with the enrollment as it now is. That answer becomes the cached
 * enrollment, so the next step opens without another request and the step guard sees the new step.
 */
function useWizardMutation<TVars>(
  fn: (vars: TVars) => Promise<EnrollmentDto>,
  alsoStale: string[][] = [],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (enrollment) => {
      queryClient.setQueryData(oneKey(enrollment.id), enrollment);
      return Promise.all(
        [['enrollments', 'list'], ['enrollments', 'costs', enrollment.id], ...alsoStale].map(
          (queryKey) => queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

export const useStartEnrollment = () =>
  useWizardMutation((input: EnrollmentStartInput) =>
    api.post<EnrollmentDto>('/enrollments', input),
  );

export const useSaveStep2 = (id: string) =>
  useWizardMutation((input: EnrollmentStep2Input) =>
    api.put<EnrollmentDto>(`/enrollments/${id}/step2`, input),
  );

export const useSaveStep3 = (id: string) =>
  useWizardMutation((input: EnrollmentStep3Input) =>
    api.put<EnrollmentDto>(`/enrollments/${id}/step3`, input),
  );

/** Payment and activation. A finished sale uses a SIM and creates a customer. */
export function useCompleteEnrollment(id: string) {
  const queryClient = useQueryClient();
  const mutation = useWizardMutation(
    (input: EnrollmentPaymentInput) =>
      api.post<EnrollmentDto>(`/enrollments/${id}/complete`, input),
    [['inventory'], ['customers']],
  );
  /** After a refused attempt the stored enrollment carries the reason; read it again. */
  const refresh = () => queryClient.invalidateQueries({ queryKey: oneKey(id) });
  return { ...mutation, refresh };
}
